import { LogoutModal } from '@components/LogoutModal';
import { Loader } from '@gurupay/gp-ui-library';
import { useLocalStorage } from '@hooks/useLocalStorage.ts';
import type { Session } from '@ory/client';
import { buildStepUpUrl, rememberLoginRedirect } from '@utils/loginRedirect';
import { getInfoFromToken } from '@utils/utils';
import { isAxiosError } from 'axios';
import { differenceInSeconds } from 'date-fns';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useEffect, useState } from 'react';

import { ory } from '../services/oryClient';
import { requestSessionExtending } from '../services/sessionExtend';

export interface AuthContextParams {
	session?: Session;
	isLoading: boolean;
	logout: () => Promise<void>;
	signOut: () => Promise<void>;
	login: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextParams | null>(null);

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
	const [token, setToken] = useLocalStorage('token');
	const { session, expireAt, issuedAt } = getInfoFromToken(token);
	const timeOffset = issuedAt ? Date.now() - issuedAt * 1000 : 0;
	const [isLogingOut, setIsLogingOut] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	// Get ORY token and set it to storage
	const login = async () => {
		if (isLogingOut) {
			return;
		}

		try {
			const { data } = await ory.toSession({
				tokenizeAs: import.meta.env.VITE_ORY_JWT_TEMPLATE_NAME,
			});
			setToken(data.tokenized);
			setIsLoading(false);
		} catch (err: unknown) {
			if (isAxiosError(err) && err.status === 403) {
				const errorId = err?.response?.data?.error?.id;
				const redirectTo = err?.response?.data?.redirect_browser_to;
				if (errorId === 'session_aal2_required' && redirectTo) {
					rememberLoginRedirect();
					window.location.href = buildStepUpUrl(redirectTo);
					return;
				}
			}

			throw err;
		}
	};

	// Extend the session and request a new token as session extending do not return new token itself, in case of error session could not be extending => perform logout
	const extendSession = async () => {
		try {
			await requestSessionExtending(token);
			await login();
		} catch {
			await logout();
		}
	};

	// Clear session in ORY, remove token from storage redirect user to the login page
	const logout = async (isManuallyTriggered?: boolean) => {
		setIsLoading(true);
		setIsLogingOut(true);
		rememberLoginRedirect();
		const returnTo = encodeURIComponent(window.location.href);
		const loginUrl = `${import.meta.env.VITE_ORY_BASE_PATH}/self-service/login/browser?return_to=${returnTo}${isManuallyTriggered ? '&is_manually_triggered=true' : ''}`;

		if (token) {
			try {
				const { data } = await ory.createBrowserLogoutFlow({ returnTo: loginUrl });
				setToken(undefined);
				window.location.href = data.logout_url;
				return;
			} catch (error: unknown) {
				const message = error instanceof Error ? error.message : String(error);
				console.error(`Error while logout: ${message}`);
				// createBrowserLogoutFlow failed — session is likely already invalid,
				// so falling through to redirect to login is safe (no active Ory session to loop back).
			}
		}

		setToken(undefined);
		window.location.href = loginUrl;
		setIsLoading(false);
	};

	const signOut = () => logout(true);

	// need to get token from ory as token life span could be less than session lifetime (kind of both get and refresh token flow)
	const requestToken = async () => {
		try {
			await login();
		} catch {
			await logout();
		}
	};

	// Sets time point in future when token is going to expire and request a new one
	useEffect(() => {
		const diff = expireAt * 1000 - Date.now() - 1000;
		if (diff <= 0) {
			return;
		}

		const anim = (document.body?.animate([], { duration: diff }) as Animation) ?? null;
		if (anim) {
			anim.onfinish = requestToken;
		}

		return () => anim?.cancel();
	}, [expireAt]);

	// On component mount parse and check if the token is valid otherwise request a new one
	useEffect(() => {
		const now = new Date();
		const diff = expireAt ? differenceInSeconds(expireAt * 1000, now) : 0;

		if (token && diff > 0) {
			setIsLoading(false);
		} else {
			requestToken();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expireAt]);

	return (
		<>
			{!!session?.expires_at && (
				<LogoutModal
					expiresAt={session?.expires_at}
					extendSession={extendSession}
					logout={logout}
					signOut={signOut}
					timeOffset={timeOffset}
				/>
			)}
			<AuthContext.Provider value={{ session, isLoading, logout, signOut, login }}>
				{isLoading ? (
					<div className="h-[100vh] flex items-center justify-center">
						<Loader />
					</div>
				) : (
					children
				)}
			</AuthContext.Provider>
		</>
	);
};
